import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { RequirePlan } from "../components/RouteGuard";
import { Shop } from "@solar-icons/react";

interface Submission {
  id: string;
  reference_id: string;
  shop_domain: string;
  customer_name: string | null;
  customer_email: string;
  product_title: string | null;
  condition: string;
  quote_amount: number;
  final_amount: number | null;
  payout_method: string;
  status: "pending" | "approved" | "rejected";
  discount_code: string | null;
  created_at: string;
}

interface Stats {
  pending: number;
  approved: number;
  rejected: number;
  total_value: number;
}

// Tenant-specific demo submissions
function getTenantDemoSubmissions(tenantId: string): {
  submissions: Submission[];
  stats: Stats;
} {
  if (tenantId === "arcteryx") {
    const submissions: Submission[] = [
      {
        id: "sub-arc-001",
        reference_id: "ARC-TI-2024-001",
        shop_domain: "arcteryx-rebird.myshopify.com",
        customer_name: "Alex Thompson",
        customer_email: "alex.t@example.com",
        product_title: "Beta AR Jacket - Men's Medium",
        condition: "excellent",
        quote_amount: 280,
        final_amount: 280,
        payout_method: "store-credit",
        status: "approved",
        discount_code: "REBIRD-ARC001-280",
        created_at: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: "sub-arc-002",
        reference_id: "ARC-TI-2024-002",
        shop_domain: "arcteryx-rebird.myshopify.com",
        customer_name: "Jordan McKay",
        customer_email: "jordan.m@example.com",
        product_title: "Atom LT Hoody - Women's Small",
        condition: "good",
        quote_amount: 120,
        final_amount: null,
        payout_method: "store-credit",
        status: "pending",
        discount_code: null,
        created_at: new Date(Date.now() - 7200000).toISOString(),
      },
      {
        id: "sub-arc-003",
        reference_id: "ARC-TI-2024-003",
        shop_domain: "arcteryx-rebird.myshopify.com",
        customer_name: "Sam Rivera",
        customer_email: "sam.r@example.com",
        product_title: "Cerium LT Jacket - Men's Large",
        condition: "fair",
        quote_amount: 95,
        final_amount: null,
        payout_method: "store-credit",
        status: "pending",
        discount_code: null,
        created_at: new Date(Date.now() - 14400000).toISOString(),
      },
      {
        id: "sub-arc-004",
        reference_id: "ARC-TI-2024-004",
        shop_domain: "arcteryx-rebird.myshopify.com",
        customer_name: "Taylor Chen",
        customer_email: "taylor.c@example.com",
        product_title: "Alpha SV Jacket - Men's Medium",
        condition: "excellent",
        quote_amount: 450,
        final_amount: 450,
        payout_method: "store-credit",
        status: "approved",
        discount_code: "REBIRD-ARC004-450",
        created_at: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: "sub-arc-005",
        reference_id: "ARC-TI-2024-005",
        shop_domain: "arcteryx-rebird.myshopify.com",
        customer_name: "Casey Williams",
        customer_email: "casey.w@example.com",
        product_title: "Proton FL Hoody - Women's Medium",
        condition: "poor",
        quote_amount: 40,
        final_amount: 0,
        payout_method: "store-credit",
        status: "rejected",
        discount_code: null,
        created_at: new Date(Date.now() - 172800000).toISOString(),
      },
    ];
    return {
      submissions,
      stats: { pending: 2, approved: 2, rejected: 1, total_value: 985 },
    };
  } else if (tenantId === "nespresso") {
    const submissions: Submission[] = [
      {
        id: "sub-nes-001",
        reference_id: "NES-RC-2024-001",
        shop_domain: "nespresso-recycling.myshopify.com",
        customer_name: "Sarah Miller",
        customer_email: "sarah.m@example.com",
        product_title: "Used Capsules (45 count)",
        condition: "recycled",
        quote_amount: 90,
        final_amount: 90,
        payout_method: "loyalty-points",
        status: "approved",
        discount_code: null,
        created_at: new Date(Date.now() - 3600000).toISOString(),
      },
    ];
    return {
      submissions,
      stats: { pending: 0, approved: 1, rejected: 0, total_value: 90 },
    };
  } else if (tenantId === "nike") {
    const submissions: Submission[] = [
      {
        id: "sub-nike-001",
        reference_id: "NIKE-TI-2025-001",
        shop_domain: "nike.com",
        customer_name: "Marcus Johnson",
        customer_email: "marcus.j@example.com",
        product_title: "Air Max 90 - Men's 10",
        condition: "excellent",
        quote_amount: 65,
        final_amount: 65,
        payout_method: "store-credit",
        status: "approved",
        discount_code: "NIKE-RECYCLE-001-65",
        created_at: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: "sub-nike-002",
        reference_id: "NIKE-TI-2025-002",
        shop_domain: "nike.com",
        customer_name: "Jessica Williams",
        customer_email: "jessica.w@example.com",
        product_title: "Air Jordan 1 Retro High OG - Men's 9.5",
        condition: "good",
        quote_amount: 95,
        final_amount: null,
        payout_method: "store-credit",
        status: "pending",
        discount_code: null,
        created_at: new Date(Date.now() - 7200000).toISOString(),
      },
      {
        id: "sub-nike-003",
        reference_id: "NIKE-TI-2025-003",
        shop_domain: "nike.com",
        customer_name: "David Chen",
        customer_email: "david.c@example.com",
        product_title: "Tech Fleece Hoodie - Men's L",
        condition: "excellent",
        quote_amount: 45,
        final_amount: null,
        payout_method: "store-credit",
        status: "pending",
        discount_code: null,
        created_at: new Date(Date.now() - 14400000).toISOString(),
      },
      {
        id: "sub-nike-004",
        reference_id: "NIKE-TI-2025-004",
        shop_domain: "nike.com",
        customer_name: "Sarah Martinez",
        customer_email: "sarah.m@example.com",
        product_title: "Air Force 1 '07 - Women's 8",
        condition: "fair",
        quote_amount: 35,
        final_amount: 35,
        payout_method: "store-credit",
        status: "approved",
        discount_code: "NIKE-RECYCLE-004-35",
        created_at: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: "sub-nike-005",
        reference_id: "NIKE-TI-2025-005",
        shop_domain: "nike.com",
        customer_name: "Chris Taylor",
        customer_email: "chris.t@example.com",
        product_title: "Dunk Low - Men's 11",
        condition: "poor",
        quote_amount: 25,
        final_amount: 0,
        payout_method: "store-credit",
        status: "rejected",
        discount_code: null,
        created_at: new Date(Date.now() - 172800000).toISOString(),
      },
    ];
    return {
      submissions,
      stats: { pending: 2, approved: 2, rejected: 1, total_value: 265 },
    };
  } else if (tenantId === "lululemon") {
    const submissions: Submission[] = [
      {
        id: "sub-lulu-001",
        reference_id: "LULU-TI-2024-001",
        shop_domain: "lululemon-liketnew.myshopify.com",
        customer_name: "Emily Ross",
        customer_email: "emily.r@example.com",
        product_title: 'Align High-Rise Pant 25"',
        condition: "excellent",
        quote_amount: 35,
        final_amount: 35,
        payout_method: "store-credit",
        status: "approved",
        discount_code: "LIKENEW-LULU001-35",
        created_at: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: "sub-lulu-002",
        reference_id: "LULU-TI-2024-002",
        shop_domain: "lululemon-liketnew.myshopify.com",
        customer_name: "Mike Chen",
        customer_email: "mike.c@example.com",
        product_title: "Scuba Oversized Half-Zip",
        condition: "good",
        quote_amount: 45,
        final_amount: null,
        payout_method: "store-credit",
        status: "pending",
        discount_code: null,
        created_at: new Date(Date.now() - 7200000).toISOString(),
      },
    ];
    return {
      submissions,
      stats: { pending: 1, approved: 1, rejected: 0, total_value: 80 },
    };
  }

  // Default: return empty for unknown tenants
  return {
    submissions: [],
    stats: { pending: 0, approved: 0, rejected: 0, total_value: 0 },
  };
}

export const Route = createFileRoute("/shopify-trade-ins")({
  component: () => (
    <RequirePlan required="starter" featureName="Shopify Trade-ins" icon={<Shop size={64} weight="LineDuotone" />}>
      <ShopifyTradeIns />
    </RequirePlan>
  ),
});

function ShopifyTradeIns() {
  const { currentTenant } = useTenant();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [stats, setStats] = useState<Stats>({
    pending: 0,
    approved: 0,
    rejected: 0,
    total_value: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, [currentTenant.id]);

  const fetchSubmissions = async () => {
    try {
      const res = await fetch(
        `/api/shopify/submissions?tenantId=${encodeURIComponent(currentTenant.id)}`,
      );
      if (res.ok) {
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await res.json();
          if (data.submissions && data.submissions.length > 0) {
            setSubmissions(data.submissions);
            setStats(
              data.stats || {
                pending: 0,
                approved: 0,
                rejected: 0,
                total_value: 0,
              },
            );
            setLoading(false);
            return;
          }
        }
      }
      // Use demo data
      const demoData = getTenantDemoSubmissions(currentTenant.id);
      setSubmissions(demoData.submissions);
      setStats(demoData.stats);
    } catch (err) {
      console.debug("Failed to fetch submissions, using demo data:", err);
      const demoData = getTenantDemoSubmissions(currentTenant.id);
      setSubmissions(demoData.submissions);
      setStats(demoData.stats);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    id: string,
    status: "approved" | "rejected",
    finalAmount?: number,
  ) => {
    try {
      const res = await fetch(`/api/shopify/submissions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, final_amount: finalAmount }),
      });

      if (res.ok) {
        fetchSubmissions();
        setSelectedSubmission(null);
      }
    } catch (err) {
      console.error("Failed to update submission:", err);
    }
  };

  const filteredSubmissions =
    filter === "all"
      ? submissions
      : submissions.filter((s) => s.status === filter);

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      pending: { bg: "#FEF3C7", text: "#92400E" },
      approved: { bg: "#D1FAE5", text: "#065F46" },
      rejected: { bg: "#FEE2E2", text: "#991B1B" },
    };
    const style = styles[status] || styles.pending;
    return (
      <span
        className="px-2 py-1 rounded-full text-xs font-medium"
        style={{ backgroundColor: style.bg, color: style.text }}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  // Get tenant-specific title
  const getPageTitle = () => {
    if (currentTenant.id === "nike") return "Nike.com Trade-Ins";
    if (currentTenant.id === "arcteryx") return "Arc'teryx ReBird Trade-Ins";
    if (currentTenant.id === "nespresso") return "Nespresso Recycling";
    return "Shopify Trade-Ins";
  };

  const getPageSubtitle = () => {
    if (currentTenant.id === "nike")
      return "Manage trade-in submissions from Nike.com";
    if (currentTenant.id === "arcteryx")
      return "Manage trade-in submissions from the ReBird program";
    if (currentTenant.id === "nespresso")
      return "Manage capsule recycling submissions";
    return "Manage trade-in submissions from your Shopify stores";
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold" style={{ color: "#171717" }}>
          {getPageTitle()}
        </h1>
        <p className="mt-1" style={{ color: "#A9A9A9" }}>
          {getPageSubtitle()}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Pending Review" value={stats.pending} highlight />
        <StatCard label="Total Submissions" value={submissions.length} />
        <StatCard label="Approved" value={stats.approved} />
        <StatCard
          label="Total Value"
          value={`$${stats.total_value.toFixed(0)}`}
        />
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(["all", "pending", "approved", "rejected"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={
              filter === tab
                ? { backgroundColor: "#171717", color: "#FFFFFF" }
                : { backgroundColor: "#F5F5F5", color: "#666666" }
            }
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === "all" && ` (${submissions.length})`}
            {tab === "pending" && ` (${stats.pending})`}
            {tab === "approved" && ` (${stats.approved})`}
            {tab === "rejected" && ` (${stats.rejected})`}
          </button>
        ))}
      </div>

      {/* Submissions Table */}
      <div
        className="bg-white border"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        {filteredSubmissions.length === 0 ? (
          <div className="p-12 text-center" style={{ color: "#A9A9A9" }}>
            <div className="text-4xl mb-4">📦</div>
            <p>No {filter === "all" ? "" : filter} submissions yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Reference
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Customer
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Product
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Condition
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Quote
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Status
                </th>
                <th
                  className="text-left px-6 py-4 text-xs font-medium uppercase tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => (
                <tr
                  key={submission.id}
                  onClick={() => setSelectedSubmission(submission)}
                  className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ borderColor: "rgba(169,169,169,0.1)" }}
                >
                  <td className="px-6 py-4">
                    <span
                      className="font-mono font-medium"
                      style={{ color: "#171717" }}
                    >
                      {submission.reference_id}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        {submission.customer_name || "N/A"}
                      </p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        {submission.customer_email}
                      </p>
                    </div>
                  </td>
                  <td
                    className="px-6 py-4 text-sm"
                    style={{ color: "#171717" }}
                  >
                    {submission.product_title || "N/A"}
                  </td>
                  <td
                    className="px-6 py-4 text-sm capitalize"
                    style={{ color: "#171717" }}
                  >
                    {submission.condition}
                  </td>
                  <td
                    className="px-6 py-4 text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    ${submission.quote_amount}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(submission.status)}
                  </td>
                  <td
                    className="px-6 py-4 text-sm"
                    style={{ color: "#A9A9A9" }}
                  >
                    {new Date(submission.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <SubmissionModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      className="p-6"
      style={
        highlight
          ? { background: "linear-gradient(135deg, #00FBA9 0%, #00D090 100%)" }
          : {
              backgroundColor: "#FFFFFF",
              border: "1px solid rgba(169,169,169,0.2)",
            }
      }
    >
      <p
        className="text-xs font-medium uppercase tracking-wider mb-2"
        style={{ color: highlight ? "rgba(0,0,0,0.6)" : "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="text-3xl font-bold"
        style={{ color: highlight ? "#171717" : "#171717" }}
      >
        {value}
      </p>
    </div>
  );
}

function SubmissionModal({
  submission,
  onClose,
  onStatusUpdate,
}: {
  submission: Submission;
  onClose: () => void;
  onStatusUpdate: (
    id: string,
    status: "approved" | "rejected",
    finalAmount?: number,
  ) => void;
}) {
  const [finalAmount, setFinalAmount] = useState(submission.quote_amount);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <h2 className="text-lg font-semibold" style={{ color: "#171717" }}>
            Trade-In Details
          </h2>
          <button
            onClick={onClose}
            className="text-2xl"
            style={{ color: "#A9A9A9" }}
          >
            &times;
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Reference
              </p>
              <p className="font-mono font-medium" style={{ color: "#171717" }}>
                {submission.reference_id}
              </p>
            </div>
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Status
              </p>
              <p className="capitalize" style={{ color: "#171717" }}>
                {submission.status}
              </p>
            </div>
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Customer
              </p>
              <p style={{ color: "#171717" }}>
                {submission.customer_name || "N/A"}
              </p>
              <p className="text-sm" style={{ color: "#A9A9A9" }}>
                {submission.customer_email}
              </p>
            </div>
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Payout Method
              </p>
              <p className="capitalize" style={{ color: "#171717" }}>
                {submission.payout_method?.replace("-", " ")}
              </p>
            </div>
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Product
              </p>
              <p style={{ color: "#171717" }}>
                {submission.product_title || "N/A"}
              </p>
            </div>
            <div>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Condition
              </p>
              <p className="capitalize" style={{ color: "#171717" }}>
                {submission.condition}
              </p>
            </div>
          </div>

          {submission.discount_code && (
            <div className="p-4" style={{ backgroundColor: "#F0FDF4" }}>
              <p
                className="text-xs uppercase tracking-wider mb-1"
                style={{ color: "#065F46" }}
              >
                Discount Code
              </p>
              <p className="font-mono font-bold" style={{ color: "#065F46" }}>
                {submission.discount_code}
              </p>
            </div>
          )}

          {submission.status === "pending" && (
            <div>
              <label
                className="text-xs uppercase tracking-wider mb-2 block"
                style={{ color: "#A9A9A9" }}
              >
                Final Amount
              </label>
              <input
                type="number"
                value={finalAmount}
                onChange={(e) =>
                  setFinalAmount(parseFloat(e.target.value) || 0)
                }
                className="w-full px-4 py-3 border text-lg font-medium"
                style={{
                  borderColor: "rgba(169,169,169,0.3)",
                  color: "#171717",
                }}
              />
            </div>
          )}
        </div>

        <div
          className="px-6 py-4 border-t flex justify-end gap-3"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-medium"
            style={{ backgroundColor: "#F5F5F5", color: "#666666" }}
          >
            Close
          </button>
          {submission.status === "pending" && (
            <>
              <button
                onClick={() => onStatusUpdate(submission.id, "rejected")}
                className="px-4 py-2 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: "#EF4444" }}
              >
                Reject
              </button>
              <button
                onClick={() =>
                  onStatusUpdate(submission.id, "approved", finalAmount)
                }
                className="px-4 py-2 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: "#10B981" }}
              >
                Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
